import React, { useState, useEffect } from 'react';
import { withTheme, withStyles } from '@material-ui/core';
import { compose } from 'recompose';
import { withAppConfig } from '../../../../../appConfigContext';
import Map, { Marker, MapConstant, MapControls, Popup } from '../../../../../lib/Map';
import { withSnackbar } from 'notistack';
import {
  isActualLocationAvailable,
  isDriverDelayed,
  renderStopMarker,
  renderLocationMarker,
  getExistingFeaturesGeoJSON
} from '../Utils';
import DrawingTools from '../../../Settings/common/DrawTools/DrawingTools';
import DrawStyles from '../../../../../lib/Map/Trimble/components/DrawStyles';
import Events from '../../../../../utilities/EventEmitter';
import { getDataWithQueryString } from '../../../../../api/componentAPIs';
import { withUserProfile } from '../../../../../userProfile/userProfileContext';
import appConstants from '@appConstants';
import MESSAGES from '../../../../../appMessages';
import CustomerMergeIndicator from '../../../../common/CustomerMergeIndicator';
import { getDispatchDriverMarker } from '../../../../mapComponents/mapHelper';
import { SpottedTrailerMarker } from '../../../../mapComponents/Markers';
import { SpottedTrailerPopupInfo } from '../../../../mapComponents/Popups';
import { formatAddress } from '../../../Dispatch/utils/utils';
import { STOP_MAP_ZOOM, STOP_TYPE } from '../constants';
import StopGeofenceEntryExitMarker from '../Common/StopGeofenceEntryExitMarker';
import {
  buildCustomerGeoFenceQueryString,
  getGeoFenceFromResponse
} from '../../../Common/GeoFence/utils';

const styles = (theme) => ({
  map: {
    display: 'flex',
    flexDirection: 'column',
    height: '60%'
  },
  headerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `1px solid ${theme.AppBGColor}`,
    marginBottom: 1,
    borderRadius: '4px 4px 0 0',
    padding: 9,
    fontSize: theme.headerTextFontSize,
    lineHeight: '16px',
    color: theme.HeaderSelectedText
  },
  label: {
    marginLeft: 6
  }
});

const StopLocationMap = ({
  theme,
  classes,
  stopData,
  configJSON,
  userData,
  driverData,
  enqueueSnackbar
}) => {
  const [geoDataList, setGeoDataList] = useState([]);
  const [selectedTrailerStop, setSelectedTrailerStop] = useState(null);
  useEffect(() => {
    if (!stopData?.customerCode || !userData?.userProfile?.terminal?.id) {
      return;
    }
    getCustomerGeoFence(
      buildCustomerGeoFenceQueryString({
        terminalId: userData.userProfile.terminal.id,
        customerCode: stopData.customerCode,
        rmaxShipperCode:
          stopData.stopType === STOP_TYPE.Pickup ? stopData.rmaxShipperCode : undefined
      })
    );
  }, [
    stopData?.customerCode,
    stopData?.stopType,
    stopData?.rmaxShipperCode,
    userData?.userProfile?.terminal?.id
  ]);

  const handleCustomerGeoFenceFetchFailure = () => {
    setGeoDataList([]);
    enqueueSnackbar(MESSAGES.COMMON.CUSTOMER_GEOFENCE_FETCH_FAILED, {
      variant: 'error'
    });
  };

  const getCustomerGeoFence = (queryString) => {
    getDataWithQueryString(
      'GetCustomerGeoFence',
      queryString
    ).then(
      (response) => {
        if (response?.status >= 200 && response?.status < 400) {
          const geoFence = getGeoFenceFromResponse(response);
          setGeoDataList(
            getExistingFeaturesGeoJSON({
              geofence: geoFence,
              id: stopData.stopId
            })
          );
          return;
        }
        handleCustomerGeoFenceFetchFailure();
      },
      handleCustomerGeoFenceFetchFailure
    );
  };

  const mapCenter = {
    lat: stopData.consigneeDetails.latitude,
    lng: stopData.consigneeDetails.longitude
  };

  const colors = {
    color: theme.HeaderSelectedText,
    textColor: theme.routeMapColorPalette.MarkerTextColor
  };

  const getSpottedTrailerPopupData = (stopData) => {
    const fullAddress = formatAddress({
      address: stopData.consigneeDetails?.address,
      city: stopData.consigneeDetails?.city,
      state: stopData.consigneeDetails?.state,
      zip: stopData.consigneeDetails?.zip
    });
    return {
      customerName: (
        <>
          {stopData.consigneeDetails?.displayCustomerName}
          <CustomerMergeIndicator
            displayCustomerName={stopData.consigneeDetails?.displayCustomerName}
            customerName={stopData.consigneeDetails?.name}
          />
        </>
      ),
      spottedAddress: fullAddress,
      customerCode: '',
      trailerNumber: driverData.trailerNumber,
      trailerScac: '',
      trailerName: driverData.trailerName,
      trailerStatus: '',
      driverName: driverData.driverName,
      driverNumber: driverData.driverNumber,
      spottedSince: stopData.actualDepartureTime
    };
  };

  const onTrailerIconClick = (selectedTrailerStop) => {
    setSelectedTrailerStop(selectedTrailerStop);
  };

  const renderStopLocationMarker = () => (
    <Marker
      data={{ ...stopData }}
      position={mapCenter}
      key={`${stopData.stopId}_${stopData.stopSequence}`}
      zIndex={1}
      isActiveShipment
      easeToAnimation
      mapZoomLevel={17}
      onClick={null}>
      {isActualLocationAvailable(stopData)
        ? renderLocationMarker(stopData, colors)
        : renderStopMarker(stopData, colors)}
    </Marker>
  );

  return (
    <div className={classes.map}>
      <div className={classes.headerStyle}>
        <span className={classes.label}>Stop Location Map</span>
      </div>
      <Map
        zoom={STOP_MAP_ZOOM}
        initialCenter={mapCenter}
        center={mapCenter}
        mapId={appConstants.mapIds.STOP_LOCATION_MAP}
        mapTheme={theme.themeName}
        eventHub={null}
        mapConfig={configJSON?.maps}
        onClick={null}
        satteliteView>
        <MapControls
          controlOptions={{
            zoomControl: {
              position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT
            },
            satelliteViewControl: {
              position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT
            },
            reCenterMapControl: {
              position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT
            }
          }}
        />

        <DrawingTools
          polygonOptions={{
            fillColor: theme.MapDrawingColor,
            strokeColor: theme.MapDrawingColor
          }}
          geoDataList={geoDataList}
          styleArray={DrawStyles(theme.commonAppHeaderTabColor)}
          eventHub={Events}
          featureIds={stopData.stopId}
        />

        {stopData &&
          <StopGeofenceEntryExitMarker
            entry={{
              lat: stopData.geofenceEntryLatitude,
              lng: stopData.geofenceEntryLongitude,
              time: stopData.geoFenceArrivalTime
            }}
            exit={{
              lat: stopData.geofenceExitLatitude,
              lng: stopData.geofenceExitLongitude,
              time: stopData.geoFenceDepartureTime
            }}
            theme={theme}
            key={stopData.stopId}
          />
        }

        {driverData?.isLiveDispatch &&
          driverData?.lastKnownLongitude &&
          driverData?.lastKnownLatitude && (
            <Marker
              position={{ lat: driverData.lastKnownLatitude, lng: driverData.lastKnownLongitude }}
              zIndex={1}
              key={driverData.driverId}
              data={driverData}
              onClick={null}>
              {getDispatchDriverMarker({
                data: driverData,
                isDelayed: isDriverDelayed(driverData),
                showDriverLabel: true,
                theme,
                onMessageIconClick: null
              })}
            </Marker>
          )}

        {stopData &&
          (stopData.isDropStop ? (
            <Marker
              data={{ ...stopData }}
              position={mapCenter}
              key={`${stopData.stopId}_SpottedTrailerMarker`}
              forceUpdate={true}
              onClick={onTrailerIconClick}
              isActiveShipment
              zIndex={1}>
              <SpottedTrailerMarker
                backgroundColor={theme.unassignPickupFillColor}
                borderColor={theme.markerStokeColor}
              />
            </Marker>
          ) : (
            renderStopLocationMarker()
          ))}

        {isActualLocationAvailable(stopData) && (
          <Marker
            data={{ ...stopData }}
            position={{
              lat: stopData.latitude,
              lng: stopData.longitude
            }}
            key={`${stopData.stopId}_Actual_${stopData.stopSequence}`}
            zIndex={1}
            easeToAnimation
            mapZoomLevel={17}
            onClick={null}>
            {renderStopMarker(stopData, colors)}
          </Marker>
        )}

        {selectedTrailerStop && (
          <Popup
            marker={selectedTrailerStop.marker}
            position={selectedTrailerStop.position}
            key={`${selectedTrailerStop.data.stopId}_SpottedTrailerPopup`}>
            <SpottedTrailerPopupInfo
              trailerData={getSpottedTrailerPopupData(selectedTrailerStop.data)}
              showTrailerScac={configJSON?.showTrailerScac}
            />
          </Popup>
        )}
      </Map>
    </div>
  );
};

export default compose(
  withStyles(styles),
  withTheme(),
  withSnackbar,
  withAppConfig,
  withUserProfile
)(StopLocationMap);
